import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/AppContexts';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { 
  Clock, 
  Plus, 
  Printer,
  ArrowLeft,
  Edit,
  Trash2,
  FileText,
  Save,
  Loader2,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';

const ICSActivityLogForm = ({ currentExercise }) => {
  const { theme } = useTheme();
  
  const positions = [
    'Command - Incident Commander',
    'Command - Incident Commander (deputy)',
    'Command - Safety Officer',
    'Command - Information Officer',
    'Command - Liaison Officer',
    'Operations - Section Chief',
    'Operations - Air Ops Branch Director',
    'Operations - Branch 1 Director',
    'Operations - Branch 2 Director',
    'Operations - Air Support Group Supervisor',
    'Operations - Helibase Supervisor',
    'Operations - Helispot Supervisor',
    'Operations - Fixed-Wing Coordinator',
    'Operations - Air Tactical Group Supervisor',
    'Operations - Helicopter Coord',
    'Operations - Fixed-Wing Coord (Tac)',
    'Operations - Air Tanker Coord',
    'Operations - Division Group 1a Supervisor',
    'Operations - Division Group 1b Supervisor',
    'Operations - Division Group 1c Supervisor',
    'Operations - Division Group 1d Supervisor',
    'Operations - Division Group 2a Supervisor',
    'Operations - Division Group 2b Supervisor',
    'Operations - Division Group 2c Supervisor',
    'Operations - Division Group 2d Supervisor',
    'Planning - Section Chief',
    'Planning - Resources Unit - Leader',
    'Planning - Situation - Leader',
    'Planning - Documentation - Leader',
    'Planning - Demobilization Unit - Leader',
    'Planning - Tech Special',
    'Logistics - Section Chief',
    'Logistics - Branch Support - Director',
    'Logistics - Supply Unit - Leader',
    'Logistics - Facilities Unit - Leader',
    'Logistics - Ground Support Unit - Leader',
    'Logistics - Branch Services - Director',
    'Logistics - Communications Unit - Director',
    'Logistics - Medical Unit - Leader',
    'Logistics - Food Unit - Leader',
    'Finance - Section Chief',
    'Finance - Time Unit - Leader',
    'Finance - Procurement Unit - Leader',
    'Finance - Compensation/Claims Unit - Leader',
    'Finance - Cost Unit - Leader',
    'Exercise Evaluator',
    'Exercise Director',
    'Exercise Observer',
    'Team Coordinator',
    'Team Planner',
    'Team Facilitator',
    'Team Evaluator',
    'Team Task',
    'Participant',
    'Staff Member',
    'Fire Chief',
    'Police Chief',
    'Fire Fighter',
    'Medical',
    'Supervisor',
    'Security',
    'Facilitator',
    'Umpire',
    'Scribe',
    'Assistant',
    'Exercise Controller',
    'Agency Representative',
    'Owner',
    'Actor',
    'VIP',
    'Media',
    'Visitor'
  ];

  const [view, setView] = useState('list');
  const [editingLog, setEditingLog] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [collapsedEvents, setCollapsedEvents] = useState(new Set());
  // IDs of events that were just added via "Add Event" but haven't been saved yet.
  // These are pinned to the TOP of the Major Events list so the user can enter
  // data without scrolling. They drop back into chronological order once saved.
  const [newlyAddedEventIds, setNewlyAddedEventIds] = useState(new Set());
  // Common event categories used across EXRSIM exercises and real events.
  const EVENT_CATEGORIES = [
    'Decision',
    'Action',
    'Information Only',
    'Communication',
    'Resource Request',
    'Situation Update',
    'Objective Set',
    'Safety Concern',
    'Escalation',
    'Status Change',
    'Other',
  ];
  const CATEGORY_STYLES = {
    'Decision':          'bg-purple-500/20 text-purple-300 border-purple-500/40',
    'Action':            'bg-blue-500/20 text-blue-300 border-blue-500/40',
    'Information Only':  'bg-gray-500/20 text-gray-300 border-gray-500/40',
    'Communication':     'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
    'Resource Request':  'bg-amber-500/20 text-amber-300 border-amber-500/40',
    'Situation Update':  'bg-teal-500/20 text-teal-300 border-teal-500/40',
    'Objective Set':     'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
    'Safety Concern':    'bg-red-500/20 text-red-300 border-red-500/40',
    'Escalation':        'bg-orange-500/20 text-orange-300 border-orange-500/40',
    'Status Change':     'bg-indigo-500/20 text-indigo-300 border-indigo-500/40',
    'Other':             'bg-gray-500/20 text-gray-300 border-gray-500/40',
  };
  const PRIORITY_LEVELS = ['Critical', 'High', 'Medium', 'Low'];
  const PRIORITY_STYLES = {
    'Critical': 'bg-red-500/20 text-red-300 border-red-500/50',
    'High':     'bg-orange-500/20 text-orange-300 border-orange-500/50',
    'Medium':   'bg-amber-500/20 text-amber-300 border-amber-500/50',
    'Low':      'bg-green-500/20 text-green-300 border-green-500/50',
  };
  const PRIORITY_BORDER = {
    'Critical': 'border-l-4 border-l-red-500',
    'High':     'border-l-4 border-l-orange-500',
    'Medium':   'border-l-4 border-l-amber-500',
    'Low':      'border-l-4 border-l-green-500',
  };
  const PRIORITY_TEXT = {
    'Critical': 'text-red-400',
    'High':     'text-orange-400',
    'Medium':   'text-amber-400',
    'Low':      'text-green-400',
  };
  const toggleEventCollapsed = (id) => {
    setCollapsedEvents(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const formatEventDateTime = (dt) => {
    if (!dt) return 'No date/time';
    try {
      return new Date(dt).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return dt; }
  };
  const formatEventDate = (dt) => {
    // dd/mm/yyyy — parse the stored YYYY-MM-DDTHH:MM string directly to avoid
    // timezone drift from the browser's Date parser.
    if (!dt) return '--/--/----';
    const [datePart] = String(dt).split('T');
    const [y, m, d] = (datePart || '').split('-');
    if (!y || !m || !d) return '--/--/----';
    return `${d}/${m}/${y}`;
  };
  const formatEventTime = (dt) => {
    // 24-hour HH:MM.
    if (!dt) return '--:--';
    const parts = String(dt).split('T');
    return (parts[1] || '').slice(0, 5) || '--:--';
  };

  const [formData, setFormData] = useState({
    activityLogName: '',
    datePrepared: '',
    timePrepared: '',
    name: '',
    icsPosition: '',
    operatingPeriodFrom: '',
    operatingPeriodTo: '',
    majorEvents: []
  });

  const [participants, setParticipants] = useState([]);

  const getAuthHeaders = () => {
    const token = secureStorage.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchActivityLogs = useCallback(async () => {
    if (!currentExercise?.id) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/activity-log/${currentExercise.id}`,
        { headers: getAuthHeaders() }
      );
      setActivityLogs(response.data);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLoading(false);
    }
  }, [currentExercise?.id]);

  // One-time migration from localStorage to backend
  useEffect(() => {
    const migrateFromLocalStorage = async () => {
      if (!currentExercise?.id) return;
      const migrationKey = `ics214_migrated_${currentExercise.id}`;
      if (secureStorage.getPreference(migrationKey)) return;

      try {
        const savedLogs = secureStorage.getPreference('ics214_activity_logs');
        if (!savedLogs) {
          secureStorage.setPreference(migrationKey, 'true');
          return;
        }
        const allLogs = JSON.parse(savedLogs);
        const logsForExercise = allLogs.filter(log => log.exerciseId === currentExercise.id);
        if (logsForExercise.length === 0) {
          secureStorage.setPreference(migrationKey, 'true');
          return;
        }

        console.log(`Migrating ${logsForExercise.length} activity logs from localStorage to backend...`);
        for (const log of logsForExercise) {
          await axios.post(
            `${API_BASE_URL}/api/activity-log/${currentExercise.id}`,
            {
              activityLogName: log.activityLogName || log.incidentName || 'Migrated Log',
              datePrepared: log.datePrepared || '',
              timePrepared: log.timePrepared || '',
              name: log.name || '',
              icsPosition: log.icsPosition || '',
              operatingPeriodFrom: log.operatingPeriodFrom || '',
              operatingPeriodTo: log.operatingPeriodTo || '',
              majorEvents: (log.majorEvents || []).map(e => ({
                id: e.id,
                dateTime: e.dateTime || '',
                event: e.event || '',
                title: e.title || '',
                category: e.category || 'Information Only',
                priority: e.priority || 'Medium',
                created_at: e.created_at || null,
                updated_at: e.updated_at || null
              }))
            },
            { headers: getAuthHeaders() }
          );
        }
        console.log('Migration complete. Removing migrated logs from localStorage.');
        const remaining = allLogs.filter(log => log.exerciseId !== currentExercise.id);
        if (remaining.length > 0) {
          secureStorage.setPreference('ics214_activity_logs', JSON.stringify(remaining));
        } else {
          secureStorage.removePreference('ics214_activity_logs');
        }
        secureStorage.setPreference(migrationKey, 'true');
        fetchActivityLogs();
      } catch (error) {
        console.error('Error migrating activity logs from localStorage:', error);
      }
    };
    migrateFromLocalStorage();
  }, [currentExercise?.id, fetchActivityLogs]);

  useEffect(() => {
    fetchActivityLogs();
  }, [fetchActivityLogs]);

  useEffect(() => {
    const fetchParticipants = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/participants`, { headers: getAuthHeaders() });
        setParticipants(response.data);
      } catch (error) {
        console.error('Error fetching participants:', error);
      }
    };
    fetchParticipants();
  }, []);

  const resetForm = () => {
    setFormData({
      activityLogName: '',
      datePrepared: '',
      timePrepared: '',
      name: '',
      icsPosition: '',
      operatingPeriodFrom: '',
      operatingPeriodTo: '',
      majorEvents: []
    });
  };

  const handleAddNew = () => {
    resetForm();
    setEditingLog(null);
    setView('form');
  };

  const handleEdit = (log) => {
    setFormData({
      activityLogName: log.activityLogName || '',
      datePrepared: log.datePrepared || '',
      timePrepared: log.timePrepared || '',
      name: log.name || '',
      icsPosition: log.icsPosition || '',
      operatingPeriodFrom: log.operatingPeriodFrom || '',
      operatingPeriodTo: log.operatingPeriodTo || '',
      majorEvents: log.majorEvents || []
    });
    setEditingLog(log);
    setView('form');
  };

  const handleDelete = async (logId) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/activity-log/${logId}`,
        { headers: getAuthHeaders() }
      );
      setActivityLogs(prev => prev.filter(log => log.id !== logId));
    } catch (error) {
      console.error('Error deleting activity log:', error);
      alert('Failed to delete activity log.');
    }
  };

  const handleSave = async () => {
    if (!formData.activityLogName || !formData.datePrepared || !formData.timePrepared || !formData.name || 
        !formData.icsPosition || !formData.operatingPeriodFrom || !formData.operatingPeriodTo) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingLog) {
        const response = await axios.put(
          `${API_BASE_URL}/api/activity-log/${editingLog.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        setActivityLogs(prev => prev.map(log => log.id === editingLog.id ? response.data : log));
        // Keep the user on the entry list — sync local editingLog + mark every
        // major event as saved so the "● Unsaved" badges flip to "● Saved".
        setEditingLog(response.data);
        setFormData(prev => ({
          ...prev,
          majorEvents: prev.majorEvents.map(e => ({ ...e, saved: true })),
        }));
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/api/activity-log/${currentExercise.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        setActivityLogs(prev => [response.data, ...prev]);
        // Fresh log — return to the log list so the user sees their new log.
        setView('list');
        resetForm();
        setEditingLog(null);
      }
    } catch (error) {
      console.error('Error saving activity log:', error);
      alert('Failed to save activity log.');
    } finally {
      setSaving(false);
    }
  };

  const addMajorEvent = () => {
    // Prefill today's date + current HH:MM so new entries slot straight into
    // the chronological view once they're saved.
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const nowStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const nowIso = now.toISOString();
    const newEvent = {
      id: Date.now() + Math.random(),
      dateTime: nowStr,
      category: 'Information Only',
      priority: 'Medium',
      title: '',
      event: '',
      created_at: nowIso,
      updated_at: nowIso,
      saved: false
    };

    // New entries always open expanded so the user can enter data immediately,
    // regardless of the collapse state of the surrounding entries.
    setCollapsedEvents(prev => {
      if (!prev.has(newEvent.id)) return prev;
      const next = new Set(prev);
      next.delete(newEvent.id);
      return next;
    });

    // Pin this entry to the top of the list until it's saved.
    setNewlyAddedEventIds(prev => {
      const next = new Set(prev);
      next.add(newEvent.id);
      return next;
    });

    setFormData(prev => ({
      ...prev,
      majorEvents: [...prev.majorEvents, newEvent]
    }));
  };

  const updateMajorEvent = (eventId, field, value) => {
    const nowIso = new Date().toISOString();
    setFormData(prev => ({
      ...prev,
      majorEvents: prev.majorEvents.map(event =>
        event.id === eventId ? { ...event, [field]: value, updated_at: nowIso, saved: false } : event
      )
    }));
  };

  const removeMajorEvent = (eventId) => {
    setNewlyAddedEventIds(prev => {
      if (!prev.has(eventId)) return prev;
      const next = new Set(prev);
      next.delete(eventId);
      return next;
    });
    setFormData(prev => ({
      ...prev,
      majorEvents: prev.majorEvents.filter(event => event.id !== eventId)
    }));
  };

  // Ask before removing — protects against a mis-click on the trash icon.
  const confirmRemoveMajorEvent = async (eventId) => {
    const entry = formData.majorEvents.find(e => e.id === eventId);
    const label = entry?.title ? `"${entry.title}"` : 'this entry';
    if (!window.confirm(`Delete ${label}? This action cannot be undone.`)) return;

    // Compute the filtered list ONCE so we can both update local state and
    // send it to the backend — using formData via a setTimeout+handleSave
    // closure was reading the pre-delete snapshot, PUTing the entry back to
    // the server, and (worse) also exiting the form to the list view.
    const nextEvents = formData.majorEvents.filter(e => e.id !== eventId);

    setFormData(prev => ({ ...prev, majorEvents: nextEvents }));
    setNewlyAddedEventIds(prev => {
      if (!prev.has(eventId)) return prev;
      const next = new Set(prev);
      next.delete(eventId);
      return next;
    });
    setCollapsedEvents(prev => {
      if (!prev.has(eventId)) return prev;
      const next = new Set(prev);
      next.delete(eventId);
      return next;
    });

    // Persist immediately for existing logs so a refresh can't restore it.
    if (editingLog) {
      try {
        setSaving(true);
        const payload = { ...formData, majorEvents: nextEvents };
        const response = await axios.put(
          `${API_BASE_URL}/api/activity-log/${editingLog.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        setActivityLogs(prev => prev.map(log => log.id === editingLog.id ? response.data : log));
      } catch (error) {
        console.error('Error deleting entry:', error);
        alert('Failed to remove entry from the server. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  };

  // Persist just this entry (still writes the whole log — the backend stores
  // majorEvents as an array — but only bumps this entry's updated_at).
  const saveMajorEvent = async (eventId) => {
    if (!editingLog) {
      // No parent log yet — fall back to full save so the log exists first.
      handleSave();
      return;
    }
    const nowIso = new Date().toISOString();
    const updatedEvents = formData.majorEvents.map(e => e.id === eventId
      ? { ...e, updated_at: nowIso, created_at: e.created_at || nowIso, saved: true }
      : e);
    const payload = { ...formData, majorEvents: updatedEvents };
    setFormData(payload);
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/activity-log/${editingLog.id}`,
        payload,
        { headers: getAuthHeaders() }
      );
      setActivityLogs(prev => prev.map(log => log.id === editingLog.id ? response.data : log));
      // Keep the local `saved` flag on the target entry after the server round-trip.
      setFormData(prev => ({
        ...prev,
        majorEvents: prev.majorEvents.map(e => e.id === eventId ? { ...e, saved: true } : e)
      }));
      // Drop this entry from the "pinned to top" set so it slots into its
      // chronological position based on dateTime.
      setNewlyAddedEventIds(prev => {
        if (!prev.has(eventId)) return prev;
        const next = new Set(prev);
        next.delete(eventId);
        return next;
      });
      // Match the collapse state of the surrounding entries once the entry
      // is saved: if every OTHER entry is currently collapsed, collapse
      // this one too; otherwise leave it expanded (the default).
      const others = formData.majorEvents.filter(e => e.id !== eventId);
      const allOthersCollapsed =
        others.length > 0 && others.every(e => collapsedEvents.has(e.id));
      if (allOthersCollapsed) {
        setCollapsedEvents(prev => {
          if (prev.has(eventId)) return prev;
          const next = new Set(prev);
          next.add(eventId);
          return next;
        });
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Failed to save entry.');
    } finally {
      setSaving(false);
    }
  };

  const formatSavedTimestamp = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ''; }
  };

  const handlePrintICS214 = (log = null) => {
    const logToPrint = log || formData;
    
    const printContent = `
      <html>
        <head>
          <title>ICS 214 - Activity Log</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; background: white; color: black; }
            .header { text-align: center; font-size: 18px; font-weight: bold; margin-bottom: 20px; }
            .field { margin-bottom: 10px; }
            .field-label { font-weight: bold; display: inline-block; width: 120px; }
            .field-value { border-bottom: 1px solid #000; display: inline-block; min-width: 200px; padding: 2px; }
            .section-title { font-weight: bold; margin: 15px 0 5px 0; font-size: 14px; }
            .events-table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            .events-table th, .events-table td { border: 1px solid #000; padding: 5px; text-align: left; }
            .events-table th { background-color: #f5f5f5; font-weight: bold; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">ICS 214 - ACTIVITY LOG</div>
          <div class="field">
            <span class="field-label">Activity Log Name:</span>
            <span class="field-value">${logToPrint.activityLogName || 'Untitled'}</span>
          </div>
          <div class="field">
            <span class="field-label">Date Prepared:</span>
            <span class="field-value">${logToPrint.datePrepared || ''}</span>
            <span class="field-label" style="margin-left: 50px;">Time:</span>
            <span class="field-value">${logToPrint.timePrepared || ''}</span>
          </div>
          <div class="field">
            <span class="field-label">Name:</span>
            <span class="field-value">${logToPrint.name || ''}</span>
          </div>
          <div class="field">
            <span class="field-label">ICS Position:</span>
            <span class="field-value">${logToPrint.icsPosition || ''}</span>
          </div>
          <div class="section-title">Operating Period:</div>
          <div class="field">
            <span class="field-label">From:</span>
            <span class="field-value">${logToPrint.operatingPeriodFrom || ''}</span>
          </div>
          <div class="field">
            <span class="field-label">To:</span>
            <span class="field-value">${logToPrint.operatingPeriodTo || ''}</span>
          </div>
          <div class="section-title">Major Events:</div>
          <table class="events-table">
            <thead>
              <tr>
                <th style="width: 130px;">Date/Time</th>
                <th>Title</th>
                <th style="width: 140px;">Category</th>
                <th style="width: 90px;">Priority</th>
              </tr>
            </thead>
            <tbody>
              ${logToPrint.majorEvents && logToPrint.majorEvents.length > 0 ? [...logToPrint.majorEvents].sort((a, b) => (a.dateTime || '').localeCompare(b.dateTime || '')).map(event => {
                const dt = event.dateTime || '';
                const [dPart, tPart] = dt.split('T');
                const [y, m, d] = (dPart || '').split('-');
                const dateStr = (y && m && d) ? `${d}/${m}/${y}` : '';
                const timeStr = (tPart || '').slice(0, 5);
                const dateTimeCell = dateStr || timeStr ? `${dateStr} ${timeStr}`.trim() : '';
                const desc = (event.event || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                return `
                <tr>
                  <td>${dateTimeCell}</td>
                  <td>${event.title || ''}</td>
                  <td>${event.category || 'Information Only'}</td>
                  <td>${event.priority || 'Medium'}</td>
                </tr>
                <tr class="event-desc-row">
                  <td colspan="4" style="padding-left: 18px; font-style: italic; color: #444;">${desc || '<span style=\"color:#999;\">No description</span>'}</td>
                </tr>`;
              }).join('') : '<tr><td colspan="4" style="text-align: center; font-style: italic;">No major events recorded</td></tr>'}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.print();
  };

  if (view === 'form') {
    return (
      <div className="p-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => setView('list')}
                variant="ghost" 
                size="sm"
                className="text-gray-400 hover:text-white"
                data-testid="activity-log-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Activity Logs
              </Button>
            </div>
            <Button
              onClick={() => handlePrintICS214()}
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              data-testid="activity-log-print-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print ICS 214
            </Button>
          </div>

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-orange-500 flex items-center gap-2">
                <Clock className="h-6 w-6" />
                ICS 214 - Activity Log
              </CardTitle>
              <p className="text-gray-400 text-sm mt-2">
                A detailed, chronological record of all unit activities for any resource (single or team) to document what they did, serving as a fundamental record for after-action reports.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Activity Log Name */}
              <div>
                <Label className="text-gray-300">Activity Log Name: <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.activityLogName}
                  onChange={(e) => setFormData(prev => ({ ...prev, activityLogName: e.target.value }))}
                  placeholder="Enter a name for this activity log (e.g., 'Day 1 Operations', 'Night Shift Log')"
                  required
                  className="bg-gray-700 border-gray-600 text-white"
                  data-testid="activity-log-name-input"
                />
              </div>

              {/* Date/Time Prepared */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Date Prepared: <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    required
                    value={formData.datePrepared}
                    onChange={(e) => setFormData(prev => ({ ...prev, datePrepared: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    data-testid="activity-log-date-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Time Prepared: <span className="text-red-500">*</span></Label>
                  <Input
                    type="text" 
                    pattern="[0-2][0-9]:[0-5][0-9]" 
                    placeholder="HH:MM (24-hour)" 
                    maxLength="5"
                    required
                    value={formData.timePrepared}
                    onChange={(e) => setFormData(prev => ({ ...prev, timePrepared: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    data-testid="activity-log-time-input"
                  />
                </div>
              </div>

              {/* Name and ICS Position */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Name: <span className="text-red-500">*</span></Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter your name"
                    required
                    className="bg-gray-700 border-gray-600 text-white"
                    data-testid="activity-log-preparer-name-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">ICS Position: <span className="text-red-500">*</span></Label>
                  <Select 
                    value={formData.icsPosition} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, icsPosition: value }))}
                    required
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="activity-log-position-select">
                      <SelectValue placeholder="Select your ICS position" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {positions.map((position) => (
                        <SelectItem key={position} value={position} className="text-white focus:bg-gray-600">
                          {position}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Operating Period */}
              <div className="bg-gray-750 p-4 rounded-lg">
                <h3 className="text-orange-500 font-semibold mb-4">Operating Period</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">From Date/Time: <span className="text-red-500">*</span></Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="date"
                        required
                        value={formData.operatingPeriodFrom ? formData.operatingPeriodFrom.split('T')[0] : ''}
                        onChange={(e) => {
                          const date = e.target.value;
                          const time = formData.operatingPeriodFrom ? formData.operatingPeriodFrom.split('T')[1] : '00:00';
                          setFormData(prev => ({ ...prev, operatingPeriodFrom: `${date}T${time}` }));
                        }}
                        className="bg-gray-700 border-gray-600 text-white"
                        data-testid="activity-log-period-from-date"
                      />
                      <Input
                        type="text"
                        placeholder="HH:MM"
                        pattern="[0-2][0-9]:[0-5][0-9]"
                        maxLength="5"
                        required
                        value={formData.operatingPeriodFrom ? formData.operatingPeriodFrom.split('T')[1] : ''}
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^0-9:]/g, '');
                          if (value.length === 2 && !value.includes(':')) {
                            value = value + ':';
                          }
                          if (value.length <= 5) {
                            const date = formData.operatingPeriodFrom ? formData.operatingPeriodFrom.split('T')[0] : '';
                            setFormData(prev => ({ ...prev, operatingPeriodFrom: `${date}T${value}` }));
                          }
                        }}
                        className="bg-gray-700 border-gray-600 text-white"
                        data-testid="activity-log-period-from-time"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-300">To Date/Time: <span className="text-red-500">*</span></Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="date"
                        required
                        value={formData.operatingPeriodTo ? formData.operatingPeriodTo.split('T')[0] : ''}
                        onChange={(e) => {
                          const date = e.target.value;
                          const time = formData.operatingPeriodTo ? formData.operatingPeriodTo.split('T')[1] : '00:00';
                          setFormData(prev => ({ ...prev, operatingPeriodTo: `${date}T${time}` }));
                        }}
                        className="bg-gray-700 border-gray-600 text-white"
                        data-testid="activity-log-period-to-date"
                      />
                      <Input
                        type="text"
                        placeholder="HH:MM"
                        pattern="[0-2][0-9]:[0-5][0-9]"
                        maxLength="5"
                        required
                        value={formData.operatingPeriodTo ? formData.operatingPeriodTo.split('T')[1] : ''}
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^0-9:]/g, '');
                          if (value.length === 2 && !value.includes(':')) {
                            value = value + ':';
                          }
                          if (value.length <= 5) {
                            const date = formData.operatingPeriodTo ? formData.operatingPeriodTo.split('T')[0] : '';
                            setFormData(prev => ({ ...prev, operatingPeriodTo: `${date}T${value}` }));
                          }
                        }}
                        className="bg-gray-700 border-gray-600 text-white"
                        data-testid="activity-log-period-to-time"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Major Events Card */}
              <Card className="bg-gray-750 border-gray-600">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <CardTitle className="text-orange-500 flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Major Events ({formData.majorEvents.length})
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => setCollapsedEvents(new Set())}
                        variant="outline"
                        size="sm"
                        className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                        data-testid="major-events-expand-all"
                      >
                        Expand All
                      </Button>
                      <Button
                        onClick={() => setCollapsedEvents(new Set(formData.majorEvents.map(e => e.id)))}
                        variant="outline"
                        size="sm"
                        className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                        data-testid="major-events-collapse-all"
                      >
                        Collapse All
                      </Button>
                      <Button 
                        onClick={addMajorEvent}
                        size="sm"
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="activity-log-add-event-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Event
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {formData.majorEvents.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">
                      No major events recorded. Click "Add Event" to add the first entry.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {(() => {
                        // Newly-added entries stay pinned to the top; every
                        // other entry sorts chronologically by dateTime.
                        const pinned = formData.majorEvents.filter(e => newlyAddedEventIds.has(e.id));
                        const settled = formData.majorEvents
                          .filter(e => !newlyAddedEventIds.has(e.id))
                          .sort((a, b) => (a.dateTime || '').localeCompare(b.dateTime || ''));
                        return [...pinned, ...settled];
                      })().map((event) => {
                        const isCollapsed = collapsedEvents.has(event.id);
                        const category = event.category || 'Information Only';
                        const priority = event.priority || 'Medium';
                        const badgeCls = CATEGORY_STYLES[category] || CATEGORY_STYLES['Other'];
                        const priorityCls = PRIORITY_STYLES[priority] || PRIORITY_STYLES['Medium'];
                        const borderCls = PRIORITY_BORDER[priority] || PRIORITY_BORDER['Medium'];
                        return (
                          <div key={event.id} className={`p-3 bg-gray-700 rounded-lg border border-gray-600 ${borderCls}`} data-testid={`major-event-card-${event.id}`}>
                            {/* Header — always visible; single line when collapsed */}
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => toggleEventCollapsed(event.id)}
                                className="flex-1 flex items-center gap-2 text-left hover:text-orange-400 transition-colors min-w-0"
                                data-testid={`major-event-toggle-${event.id}`}
                              >
                                {isCollapsed ? <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                                <span className="text-orange-400 text-sm font-mono flex-shrink-0" title="Date (dd/mm/yyyy)">{formatEventDate(event.dateTime)}</span>
                                <span className="text-orange-400 text-sm font-mono flex-shrink-0" title="Time (HH:MM)">{formatEventTime(event.dateTime)}</span>
                                <span className="text-white text-sm truncate min-w-0">{event.title || <em className="text-gray-500">Untitled entry</em>}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${badgeCls}`}>{category}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${priorityCls}`}>{priority}</span>
                                {event.saved ? (
                                  <span className="text-xs text-green-400 flex-shrink-0" title={`Saved ${formatSavedTimestamp(event.updated_at)}`}>● Saved</span>
                                ) : (
                                  <span className="text-xs text-amber-400 flex-shrink-0" title="Unsaved changes">● Unsaved</span>
                                )}
                              </button>
                              <Button
                                onClick={() => saveMajorEvent(event.id)}
                                disabled={saving}
                                variant="outline"
                                size="sm"
                                className="border-green-500/50 text-green-400 hover:bg-green-500/10 flex-shrink-0"
                                title="Save this entry"
                                data-testid={`activity-log-save-event-${event.id}`}
                              >
                                <Save className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => confirmRemoveMajorEvent(event.id)}
                                variant="outline"
                                size="sm"
                                className="border-red-500/50 text-red-400 hover:bg-red-500/10 flex-shrink-0"
                                title="Delete this entry"
                                data-testid={`activity-log-remove-event-${event.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            {/* Body — hidden when collapsed */}
                            {!isCollapsed && (
                              <div className="mt-3 space-y-3">
                                <div className="flex flex-wrap items-end gap-3">
                                  <div className="flex-1 min-w-[160px]">
                                    <Label className="text-gray-300">Date:</Label>
                                    <Input
                                      type="date"
                                      value={event.dateTime ? event.dateTime.split('T')[0] : ''}
                                      onChange={(e) => {
                                        const date = e.target.value;
                                        const time = event.dateTime ? event.dateTime.split('T')[1] : '00:00';
                                        updateMajorEvent(event.id, 'dateTime', `${date}T${time}`);
                                      }}
                                      className="bg-gray-600 border-gray-500 text-white w-full"
                                    />
                                  </div>
                                  <div className="flex-1 min-w-[120px]">
                                    <Label className="text-gray-300">Time:</Label>
                                    <Input
                                      type="text"
                                      placeholder="HH:MM"
                                      pattern="[0-2][0-9]:[0-5][0-9]"
                                      maxLength="5"
                                      value={event.dateTime ? event.dateTime.split('T')[1] : ''}
                                      onChange={(e) => {
                                        let value = e.target.value.replace(/[^0-9:]/g, '');
                                        if (value.length === 2 && !value.includes(':')) {
                                          value = value + ':';
                                        }
                                        if (value.length <= 5) {
                                          const date = event.dateTime ? event.dateTime.split('T')[0] : '';
                                          updateMajorEvent(event.id, 'dateTime', `${date}T${value}`);
                                        }
                                      }}
                                      className="bg-gray-600 border-gray-500 text-white w-full"
                                    />
                                  </div>
                                  <div className="flex-1 min-w-[180px]">
                                    <Label className="text-gray-300">Category:</Label>
                                    <Select
                                      value={category}
                                      onValueChange={(val) => updateMajorEvent(event.id, 'category', val)}
                                    >
                                      <SelectTrigger className="bg-gray-600 border-gray-500 text-white w-full" data-testid={`major-event-category-${event.id}`}>
                                        <SelectValue placeholder="Select category" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-gray-700 border-gray-600">
                                        {EVENT_CATEGORIES.map(cat => (
                                          <SelectItem key={cat} value={cat} className="text-white focus:bg-gray-600">{cat}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="flex-1 min-w-[150px]">
                                    <Label className="text-gray-300">Priority:</Label>
                                    <Select
                                      value={priority}
                                      onValueChange={(val) => updateMajorEvent(event.id, 'priority', val)}
                                    >
                                      <SelectTrigger className={`bg-gray-600 border-gray-500 w-full font-semibold ${PRIORITY_TEXT[priority] || 'text-white'}`} data-testid={`major-event-priority-${event.id}`}>
                                        <SelectValue placeholder="Select priority" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-gray-700 border-gray-600">
                                        {PRIORITY_LEVELS.map(p => (
                                          <SelectItem key={p} value={p} className={`${PRIORITY_TEXT[p]} font-semibold focus:bg-gray-600`}>{p}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-gray-300">Title:</Label>
                                  <Input
                                    type="text"
                                    value={event.title || ''}
                                    onChange={(e) => updateMajorEvent(event.id, 'title', e.target.value)}
                                    placeholder="One-line summary of the event"
                                    className="bg-gray-600 border-gray-500 text-white w-full"
                                  />
                                </div>
                                <div>
                                  <Label className="text-gray-300">Description:</Label>
                                  <Textarea
                                    value={event.event || ''}
                                    onChange={(e) => updateMajorEvent(event.id, 'event', e.target.value)}
                                    placeholder="Describe the major event, its context, and outcome..."
                                    className="bg-gray-600 border-gray-500 text-white min-h-[80px] w-full"
                                  />
                                </div>
                                {/* Audit trail — when this entry was created / last modified. */}
                                {(event.created_at || event.updated_at) && (
                                  <div className="flex flex-wrap items-center gap-4 pt-1 text-xs text-gray-500">
                                    {event.created_at && (
                                      <span>Created {formatSavedTimestamp(event.created_at)}</span>
                                    )}
                                    {event.updated_at && event.updated_at !== event.created_at && (
                                      <span>Last edited {formatSavedTimestamp(event.updated_at)}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-orange-500 hover:bg-orange-600 text-white flex-1"
                  data-testid="activity-log-save-btn"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {editingLog ? 'Update Activity Log' : 'Save Activity Log'}
                </Button>
                <Button 
                  onClick={() => setView('list')}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                  data-testid="activity-log-cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="p-6 pb-24" data-testid="activity-log-list-view">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className={`text-3xl font-bold ${theme.colors.textPrimary} flex items-center gap-3`}>
              <Clock className="h-8 w-8 text-orange-500" />
              Activity Log
            </h2>
            <p className={`${theme.colors.textMuted} mt-2`}>
              {currentExercise?.exercise_name || currentExercise?.name 
                ? `ICS 214 Activity Logs for: ${currentExercise.exercise_name || currentExercise.name}`
                : 'Manage ICS 214 Activity Logs for incident documentation and event tracking'}
            </p>
          </div>
          <Button 
            onClick={handleAddNew}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            disabled={!currentExercise?.id}
            data-testid="activity-log-new-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Activity Log
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : activityLogs.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700" data-testid="activity-log-empty-state">
            <CardContent className="p-8 text-center">
              <Clock className="h-16 w-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-300 mb-2">No Activity Logs for This Exercise</h3>
              <p className="text-gray-500 mb-6">
                {currentExercise?.exercise_name || currentExercise?.name 
                  ? `Create an ICS 214 Activity Log for "${currentExercise.exercise_name || currentExercise.name}"`
                  : 'Select an exercise first, then create your ICS 214 Activity Log'}
              </p>
              <Button 
                onClick={handleAddNew}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                disabled={!currentExercise?.id}
                data-testid="activity-log-create-first-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Activity Log
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="activity-log-entries-list">
            {activityLogs.map((log) => (
              <Card key={log.id} className="bg-gray-800 border-gray-700" data-testid={`activity-log-entry-${log.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">
                          {log.activityLogName || 'Untitled Activity Log'}
                        </h3>
                        <Badge variant="outline" className="text-orange-500 border-orange-500/50">
                          ICS 214
                        </Badge>
                      </div>
                      <div className="text-gray-400 text-sm space-y-1">
                        <p><span className="font-medium">Prepared by:</span> {log.name || 'Not specified'}</p>
                        <p><span className="font-medium">Position:</span> {log.icsPosition || 'Not specified'}</p>
                        <p><span className="font-medium">Date:</span> {log.datePrepared || 'Not specified'} {log.timePrepared && `at ${log.timePrepared}`}</p>
                        <p><span className="font-medium">Major Events:</span> {log.majorEvents ? log.majorEvents.length : 0} recorded</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handlePrintICS214(log)}
                        size="sm"
                        variant="outline"
                        className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                        data-testid={`activity-log-print-${log.id}`}
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => handleEdit(log)}
                        size="sm"
                        variant="outline"
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        data-testid={`activity-log-edit-${log.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(log.id)}
                        size="sm"
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        data-testid={`activity-log-delete-${log.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ICSActivityLogForm;
